<template>
    <div class="w-100">
        <loader-component v-if="loading" />
        <div v-else-if="error">
            {{ error }}
        </div>
        <div class="default-home restaurants-page" v-else :style="themePageStyle">
            <div class="restaurants-page-header">
                <div>
                    <span class="restaurants-page-kicker">{{ themeBrandName }}</span>
                    <h1>{{ $tl("labels.routes.restaurants") }}</h1>
                    <p>Gérez vos établissements, leurs informations et leurs paramètres depuis un espace centralisé.</p>
                </div>
                <div class="restaurants-page-actions">
                    <router-link
                        v-if="has_rights_to_create_zones"
                        :to="{ name: 'restaurants.manage_zones' }"
                        class="btn btn-sm btn-success btn-circle">
                        <feather type="plus"></feather>
                        {{ $tl("labels.routes.manageAreas") }}
                    </router-link>
                    <router-link
                        :to="{ name: 'restaurants.add' }"
                        class="btn btn-sm btn-success btn-circle"
                        :class="{ disabled: !has_right_to_create_restaurant }">
                        <feather type="plus"></feather>
                        {{ $tl("labels.routes.addRestaurant") }}
                    </router-link>
                </div>
            </div>
            <div class="restaurants-summary">
                <div>
                    <span>{{ restaurants.length }}</span>
                    <small>{{ restaurants.length > 1 ? "établissements" : "établissement" }}</small>
                </div>
                <div>
                    <span>{{ accessibleRestaurantsCount }}</span>
                    <small>accessibles</small>
                </div>
            </div>
            <div class="restaurants-grid">
                <restaurant-card
                    v-for="restaurant in restaurants"
                    :key="restaurant.id"
                    :restaurant="restaurant"
                    :disabled="!canAccessRestaurant(restaurant.id)"
                    @click="editRestaurant($event.id)" />
            </div>
        </div>
    </div>
</template>

<script>
import LoaderComponent from "../../../components/LoaderComponent.vue";
import RestaurantCard from "./RestaurantCard.vue";

export default {
    name: "ListRestaurants",
    data() {
        return {
            error: null,
            loading: false,
            restaurants: [],
        };
    },
    created() {
        this.fetchRestaurants();
    },
    computed: {
        rights: function () {
            return this.$store.getters["users/formattedRights"];
        },
        has_right_to_read_restaurant() {
            return this.rights.includes("default.restaurants.read");
        },
        has_right_to_create_restaurant() {
            return this.rights.includes("default.restaurants.create");
        },
        userRestaurantsIds() {
            return this.$store.getters["users/restaurantIds"];
        },
        restaurantIds() {
            return this.restaurants.map((restaurant) => restaurant.id);
        },
        accessibleRestaurantsCount() {
            return this.restaurants.filter((restaurant) => this.canAccessRestaurant(restaurant.id)).length;
        },
        themePageStyle() {
            return {
                "--theme-main-color": this.$style["main-color"] || "#083e60",
                "--theme-main-color-rgb": this.$style["main-color-rgb"] || "8, 62, 96",
                "--theme-main-color-light": this.$style["main-color-light"] || "#30a4b7",
                "--theme-main-color-light-rgb": this.$style["main-color-light-rgb"] || "48, 164, 183",
                "--theme-default-page-background":
                    this.$style["default-page-background"] || "linear-gradient(180deg, #ffffff 0%, #f6fbfe 44%, #edf6fb 100%)",
            };
        },
        has_rights_to_create_zones() {
            return (
                this.rights.includes("default.zones.create") &&
                !this.restaurantIds.some((restaurantId) => !this.userRestaurantsIds.includes(restaurantId))
            );
        },
    },
    methods: {
        fetchRestaurants() {
            this.error = null;
            this.loading = true;
            this.restaurants = [];

            this.$store
                .dispatch("restaurants/fetchAllRestaurants")
                .then((response) => {
                    this.restaurants = response.data.data;
                    if (this.isClubMedOwnerId(this.$store.getters["users/ownerId"])) {
                        this.restaurants.sort((a, b) => a.name.localeCompare(b.name));
                    }
                    this.loading = false;
                })
                .catch((error) => {
                    if (error.response && error.response.data.message) {
                        this.error = error.response.data.message;
                    } else {
                        this.error = error.message;
                    }
                    this.loading = false;
                });
        },
        editRestaurant(restaurant_id) {
            if (this.has_right_to_read_restaurant) this.$router.push({ name: "restaurants.edit", params: { restaurant_id } });
        },
        canAccessRestaurant(restaurantId) {
            return this.has_right_to_read_restaurant && this.userRestaurantsIds.includes(restaurantId);
        },
    },
    components: {
        LoaderComponent,
        RestaurantCard,
    },
};
</script>

<style lang="scss" scoped>
.restaurants-page {
    position: relative;
    max-width: 1180px;
    padding: 34px 24px 46px;
    --theme-main-color: #083e60;
    --theme-main-color-rgb: 8, 62, 96;
    --theme-main-color-light: #30a4b7;
    --theme-main-color-light-rgb: 48, 164, 183;
    --theme-default-page-background: linear-gradient(180deg, #ffffff 0%, #f6fbfe 44%, #edf6fb 100%);
}

.restaurants-page::before {
    position: fixed;
    inset: 60px 0 0;
    z-index: -1;
    pointer-events: none;
    content: "";
    background: var(--theme-default-page-background);
}

.restaurants-page-header {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 24px;
    margin-bottom: 18px;
}

.restaurants-page-kicker {
    display: block;
    margin-bottom: 5px;
    color: var(--theme-main-color-light);
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.08em;
    line-height: 1.2;
    text-transform: uppercase;
}

.restaurants-page-header h1 {
    margin: 0;
    color: #17202a;
    font-size: 30px;
    font-weight: 800;
    line-height: 1.15;
}

.restaurants-page-header p {
    max-width: 650px;
    margin: 8px 0 0;
    color: #667085;
    font-size: 14px;
    line-height: 1.55;
}

.restaurants-page-actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 10px;
}

.restaurants-page-actions .btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-height: 34px;
    padding: 0 14px;
    font-weight: 800;
    border-radius: 999px;
    box-shadow: 0 12px 24px rgba(var(--theme-main-color-light-rgb), 0.16);
}

.restaurants-summary {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 150px));
    gap: 12px;
    margin-bottom: 22px;
}

.restaurants-summary > div {
    padding: 14px 16px;
    background: rgba(255, 255, 255, 0.9);
    border: 1px solid rgba(var(--theme-main-color-rgb), 0.1);
    border-radius: 14px;
    box-shadow: 0 14px 32px rgba(var(--theme-main-color-rgb), 0.06);
}

.restaurants-summary span {
    display: block;
    color: var(--theme-main-color);
    font-size: 22px;
    font-weight: 800;
    line-height: 1;
}

.restaurants-summary small {
    display: block;
    margin-top: 5px;
    color: #667085;
    font-size: 12px;
    font-weight: 700;
}

.restaurants-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(285px, 1fr));
    gap: 18px;
}

@media (min-width: 1100px) {
    .restaurants-grid {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }
}

@media (max-width: 767px) {
    .restaurants-page {
        padding: 24px 14px 34px;
    }

    .restaurants-page-header {
        align-items: flex-start;
        flex-direction: column;
        gap: 14px;
    }

    .restaurants-page-header h1 {
        font-size: 24px;
    }

    .restaurants-page-actions {
        justify-content: flex-start;
        width: 100%;
    }

    .restaurants-summary {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .restaurants-grid {
        grid-template-columns: 1fr;
        gap: 14px;
    }
}
</style>
